/*
  Layers: thin progress bar for a slideshow.

  Drop <layers-slide-progress> next to (or inside) a <slideshow-component>.
  It reads the slideshow's visible slides, listens to the theme's own
  `slideshow:select` event and moves a thumb along a hairline track. Used on
  the product cards and on the product page gallery in place of the counter
  pill. Slides that get shown or hidden later (a colour swatch on a card) are
  picked up through a MutationObserver so the count stays right.
*/
(() => {
  if (customElements.get('layers-slide-progress')) return;

  class LayersSlideProgress extends HTMLElement {
    connectedCallback() {
      this.slideshow =
        this.closest('slideshow-component') ||
        this.parentElement?.querySelector('slideshow-component') ||
        this.closest('.card-gallery, media-gallery')?.querySelector('slideshow-component');
      if (!this.slideshow) return;

      this.setAttribute('role', 'presentation');
      this.innerHTML = '<span class="layers-slide-progress__thumb"></span>';
      this.thumb = this.firstElementChild;

      this.onSelect = (event) => this.update(event.detail?.index);
      this.slideshow.addEventListener('slideshow:select', this.onSelect);

      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.slideshow, {
        attributes: true,
        attributeFilter: ['hidden'],
        subtree: true,
      });

      this.update();
    }

    disconnectedCallback() {
      this.slideshow?.removeEventListener('slideshow:select', this.onSelect);
      this.observer?.disconnect();
    }

    get slides() {
      return [...this.slideshow.querySelectorAll('slideshow-slide')].filter(
        (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
      );
    }

    update(index) {
      const count = this.slides.length;
      if (count < 2) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const current =
        typeof index === 'number' ? index : Number(this.slideshow.current ?? 0) || 0;
      const clamped = Math.max(0, Math.min(count - 1, current));
      this.style.setProperty('--slide-count', String(count));
      this.style.setProperty('--slide-index', String(clamped));
    }
  }

  customElements.define('layers-slide-progress', LayersSlideProgress);
})();
